import { routes } from "@constants/routes";
import { Table as BaseTable, useFetchFile, UserIcon, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import { AuthAccountOrganizationSizeChoices, GetOrganizationsQuery } from "@graphql/generated";
import { Button, Spinner, Tooltip } from "@heroui/react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface CompanyTableProps {
  organizations?: GetOrganizationsQuery;
  className?: string;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  handleLoginAsCompany?: (id: string) => void;
}

export type OrganizationItem = Partial<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetOrganizationsQuery["admin"]>["organizations"]>["edges"][number]>["node"]
  >
>;

interface NameCellProps {
  company?: OrganizationItem | null;
}

const NameCell: React.FC<NameCellProps> = ({ company }) => {
  const { data: avatarUrl, loading } = useFetchFile({
    urlPath: company?.logo?.file,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
        {loading ? (
          <Spinner size="sm" />
        ) : (
          company?.logo?.file && (
            <img src={avatarUrl} alt={company.name} className="w-8 h-8 rounded-full object-cover" />
          )
        )}
      </div>
      <div>
        <div className="font-medium">{company?.name}</div>
        <div className="text-sm text-gray-500">{company?.shortName}</div>
      </div>
    </div>
  );
};

const getSizeLabel = (size: string | null | undefined): string => {
  if (!size) return "-";

  switch (size) {
    case AuthAccountOrganizationSizeChoices.OVER_1000:
      return "Over 1000";
    case AuthAccountOrganizationSizeChoices["_1_10"]:
      return "1-10";
    case AuthAccountOrganizationSizeChoices["_10_50"]:
      return "10-50";
    case AuthAccountOrganizationSizeChoices["_50_100"]:
      return "50-100";
    case AuthAccountOrganizationSizeChoices["_100_500"]:
      return "100-500";
    case AuthAccountOrganizationSizeChoices["_500_1000"]:
      return "500-1000";
    default:
      return size;
  }
};

const CompanyTable: React.FC<CompanyTableProps> = ({
  organizations,
  className,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  handleLoginAsCompany,
}) => {
  const columns = useMemo(() => {
    return [
      {
        header: "Company",
        cell: (data: { row: { original: any } }) => {
          const organization = data.row.original;
          return <NameCell company={organization} />;
        },
      },
      {
        header: "Industry",
        cell: (data: { row: { original: any } }) => {
          return data.row.original.industry?.title || "-";
        },
      },
      {
        header: "Size",
        cell: (data: { row: { original: any } }) => {
          return getSizeLabel(data.row.original.size);
        },
      },
      {
        header: "Status",
        cell: (data: { row: { original: any } }) => {
          return <span className="capitalize">{data.row.original.status.toLowerCase() || "-"}</span>;
        },
      },
      {
        header: "Contacts",
        cell: (data: { row: { original: any } }) => {
          const contacts = data.row.original.contacts || [];
          return (
            <div className="flex flex-col gap-1">
              {contacts.map((contact: any) => (
                <div key={contact.id} className="text-sm">
                  {contact.type}: {contact.value}
                </div>
              ))}
            </div>
          );
        },
      },
      ...(handleLoginAsCompany
        ? [
            {
              header: "Actions",
              cell: (data: { row: { original: any } }) => {
                const company = data.row.original;
                return (
                  <div className="flex gap-2">
                    <Tooltip content="Login as Company">
                      <Button
                        isIconOnly
                        variant="light"
                        className="p-1 h-auto min-w-0 w-auto"
                        onPress={() => handleLoginAsCompany(company.user.id)}
                      >
                        <UserIcon className="cursor-pointer w-5 h-5 stroke-gray-500 hover:stroke-gray-700" />
                      </Button>
                    </Tooltip>
                  </div>
                );
              },
            },
          ]
        : []),
    ];
  }, [handleLoginAsCompany]);

  const data = useMemo(() => {
    return organizations?.admin?.organizations?.edges?.map((edge) => edge?.node) || [];
  }, [organizations]);

  const navigate = useNavigate();

  const handleRowClick = (row: any) => {
    navigate(routes.companies.detail.replace(":id", row?.id ?? ""));
  };

  return (
    <div className={className}>
      <BaseTable
        columns={columns}
        data={data}
        classNames={{
          table: "min-w-full",
          tr: "cursor-pointer",

          thead: "bg-gray-50",
          th: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
          td: "px-6 py-4 whitespace-nowrap",
        }}
        onRowClick={handleRowClick}
        totalItems={organizations?.admin?.organizations?.totalCount ?? 0}
        currentPage={currentPage ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSize={pageSize}
        bottomContentCustomComponent={<></>}
        enablePagination={true}
      />
    </div>
  );
};

export default CompanyTable;
